import type { PortfolioReport } from '@interfaces/portfolioReport';
import { Card } from '@ovComponents/2-component/card/card';
import {
  Table,
  TableBody,
  TableHead,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { SecurityTableRow } from '@ovComponents/4-module/widgets/portfolio/components/securityTableRow';
import { SubAccountHoldingTableCell } from '@ovComponents/4-module/widgets/portfolio/components/subAccountHoldingTableCell';
import { SubAccountTableCell } from '@ovComponents/4-module/widgets/portfolio/components/subAccountTableCell';
import { SubAccountTableHeadCell } from '@ovComponents/4-module/widgets/portfolio/components/subAccountTableHeadCell';
import {
  holdingTradeKey,
  PORTFOLIO_COLUMN_WIDTH,
  PORTFOLIO_ROW_HEIGHT,
} from '@ovComponents/4-module/widgets/portfolio/components/utils';
import { TESTID_PORTFOLIO_WIDGET_SECURITIES_TABLE_HEAD_CELL } from '@ovComponents/4-module/widgets/portfolio/portfolioTestIds';
import { Box, Typography, useTheme } from 'hephaestus/web';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveAuditPositionStatusBadge } from '../../utils/presentation/resolveAuditPositionStatusBadge';
import { formatMultiCurrencyCashBreakdownLines } from '../../utils/snapshot/aggregateMultiCurrencyCashBreakdown';
import type { AuditPortfolioSnapshot } from '../../utils/snapshot/buildAuditPortfolioSnapshot';
import {
  resolveAuditCashDeviationDisplay,
  resolveAuditModelDeviationDisplay,
  resolveAuditModelTargetCents,
  resolveAuditProposedPoolScale,
  resolveAuditSnapshotTargetPercentage,
} from '../../utils/snapshot/resolveAuditModelDeviationDisplay';
import {
  AuditModelDeviationCell,
  AuditProposedCell,
  AuditReportTargetCell,
} from './auditGridCells';

export type BulkRebalanceAuditPortfolioGridProps = {
  snapshot: AuditPortfolioSnapshot;
  viewPreTradePositions?: boolean;
};

const EMPTY_TRADES = new Map();

export const BulkRebalanceAuditPortfolioGrid = ({
  snapshot,
  viewPreTradePositions = false,
}: BulkRebalanceAuditPortfolioGridProps) => {
  const { t } = useTranslation(['bulkRebalance', 'components']);
  const theme = useTheme();
  const {
    diffRows,
    securities,
    subAccounts,
    draftTrades,
    portfolioReportForCash,
    portfolioCurrency,
    isMultiCurrencyPortfolio,
    isCashEventRebalance,
    usesReportHoldings,
    portfolioInvestableCents,
    proposedCashCents,
    multiCurrencyCashBreakdown,
  } = snapshot;
  const auditProposedSummary = !usesReportHoldings;
  const showAuditDeviationColumn = auditProposedSummary;
  const auditSummaryColumnBorder = !showAuditDeviationColumn;
  const handleHoldingChange = useCallback(() => undefined, []);
  const showPreTradeDetail = viewPreTradePositions && draftTrades.size > 0;
  const showSubAccountColumns = subAccounts.length > 0;

  const diffRowBySecurityId = useMemo(
    () => new Map(diffRows.map((row) => [row.financialProductId, row])),
    [diffRows],
  );
  const proposedPoolScale = useMemo(
    () => (auditProposedSummary ? resolveAuditProposedPoolScale(diffRows) : 1),
    [auditProposedSummary, diffRows],
  );
  const portfolioMarketValueCents =
    portfolioInvestableCents + (portfolioReportForCash?.cashCents ?? 0);

  const cashBreakdownLines = useMemo(
    () => formatMultiCurrencyCashBreakdownLines(multiCurrencyCashBreakdown),
    [multiCurrencyCashBreakdown],
  );

  if (diffRows.length === 0) {
    return (
      <Typography
        variant='labelSmall'
        color='textSecondary'
        sx={{ px: 2, pb: 1 }}
      >
        {t('bulkRebalance:drawer.noWeightDiffs')}
      </Typography>
    );
  }

  const auditRowHeight =
    showPreTradeDetail || showAuditDeviationColumn
      ? PORTFOLIO_ROW_HEIGHT.extended
      : PORTFOLIO_ROW_HEIGHT.standard;

  return (
    <Box sx={{ px: 2, pb: 1.5 }}>
      <Card sx={{ overflowX: 'auto' }}>
        <Box sx={{ p: 1, pt: 0 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeadCell
                  sx={{ minWidth: PORTFOLIO_COLUMN_WIDTH.security }}
                  data-testid={TESTID_PORTFOLIO_WIDGET_SECURITIES_TABLE_HEAD_CELL(
                    'security',
                  )}
                >
                  {t('components:portfolioTable.security')}
                </TableHeadCell>
                {showAuditDeviationColumn && (
                  <TableHeadCell
                    sx={{ minWidth: PORTFOLIO_COLUMN_WIDTH.security }}
                    data-testid={TESTID_PORTFOLIO_WIDGET_SECURITIES_TABLE_HEAD_CELL(
                      'target',
                    )}
                    number
                  >
                    {t('components:portfolioTable.target')}
                  </TableHeadCell>
                )}
                <TableHeadCell
                  sx={{
                    minWidth: PORTFOLIO_COLUMN_WIDTH.security,
                  }}
                  data-testid={TESTID_PORTFOLIO_WIDGET_SECURITIES_TABLE_HEAD_CELL(
                    auditProposedSummary ? 'proposed' : 'target',
                  )}
                  number
                >
                  {auditProposedSummary
                    ? t('bulkRebalance:drawer.auditGrid.proposed')
                    : t('components:portfolioTable.target')}
                </TableHeadCell>
                {showAuditDeviationColumn && (
                  <TableHeadCell
                    sx={{
                      ...(showSubAccountColumns
                        ? {
                            borderRight: `1px solid ${theme.color.outlineVariant}`,
                          }
                        : {}),
                      minWidth: PORTFOLIO_COLUMN_WIDTH.security,
                    }}
                    data-testid={TESTID_PORTFOLIO_WIDGET_SECURITIES_TABLE_HEAD_CELL(
                      'deviation',
                    )}
                    number
                  >
                    {t('components:portfolioTable.deviation')}
                  </TableHeadCell>
                )}
                {!auditProposedSummary && (
                  <TableHeadCell
                    sx={{ minWidth: PORTFOLIO_COLUMN_WIDTH.security }}
                    data-testid={TESTID_PORTFOLIO_WIDGET_SECURITIES_TABLE_HEAD_CELL(
                      'current',
                    )}
                    number
                  >
                    {t('components:portfolioTable.current')}
                  </TableHeadCell>
                )}
                {!auditProposedSummary && (
                  <TableHeadCell
                    sx={{
                      ...(showSubAccountColumns
                        ? {
                            borderRight: `1px solid ${theme.color.outlineVariant}`,
                          }
                        : {}),
                      minWidth: PORTFOLIO_COLUMN_WIDTH.security,
                    }}
                    data-testid={TESTID_PORTFOLIO_WIDGET_SECURITIES_TABLE_HEAD_CELL(
                      'deviation',
                    )}
                    number
                  >
                    {t('components:portfolioTable.deviation')}
                  </TableHeadCell>
                )}
                {showSubAccountColumns &&
                  subAccounts.map((subAccount, index) => (
                    <SubAccountTableHeadCell
                      key={subAccount.id}
                      subAccount={subAccount}
                      index={index}
                    />
                  ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {portfolioReportForCash && (
                <TableRow sx={{ height: auditRowHeight }}>
                  <SecurityTableRow
                    asTableCells
                    isCash
                    security={{
                      id: 'CASH',
                      ticker: t('components:portfolioTable.cash'),
                      expectedValueCents:
                        portfolioReportForCash.expectedCashCents,
                      expectedPercentage:
                        portfolioReportForCash.expectedCashPercentage ?? 0,
                      currentPriceCents: 100,
                      translatedName: t('components:canadianCash'),
                      taxRank: null,
                      financialProductTaxRank: 99,
                      valueCents: 0,
                      subAccounts: [],
                      currency: portfolioCurrency,
                    }}
                    subAccounts={subAccounts}
                    portfolioReport={portfolioReportForCash as PortfolioReport}
                    draftTrades={EMPTY_TRADES}
                    pendingTrades={EMPTY_TRADES}
                    index={0}
                    currency={portfolioCurrency}
                    isMultiCurrencyPortfolio={isMultiCurrencyPortfolio}
                    hideCurrentChangeTooltip
                    isMakeHigher={showPreTradeDetail}
                    auditProposedSummary={auditProposedSummary}
                    auditSummaryColumnBorder={auditSummaryColumnBorder}
                    auditOmitSummaryColumn={showAuditDeviationColumn}
                    cashBreakdownLines={cashBreakdownLines}
                    cashBreakdownTooltip={
                      cashBreakdownLines.length > 0
                        ? t(
                            'bulkRebalance:drawer.auditGrid.multiCurrencyCashTooltip',
                          )
                        : undefined
                    }
                  />
                  {showAuditDeviationColumn && (
                    <>
                      <AuditReportTargetCell
                        index={0}
                        expectedValueCents={
                          portfolioReportForCash.expectedCashCents
                        }
                        expectedPercentage={
                          portfolioReportForCash.expectedCashPercentage
                        }
                      />
                      <AuditProposedCell
                        index={0}
                        proposedCents={proposedCashCents}
                      />
                      <AuditModelDeviationCell
                        display={resolveAuditCashDeviationDisplay({
                          proposedCashCents,
                          expectedCashCents:
                            portfolioReportForCash.expectedCashCents,
                          portfolioMarketValueCents,
                        })}
                        index={0}
                        borderRight={showSubAccountColumns}
                      />
                    </>
                  )}
                  {showSubAccountColumns &&
                    subAccounts.map((subAccount, index) => (
                      <SubAccountTableCell
                        key={subAccount.id}
                        subAccount={subAccount}
                        draftTrades={draftTrades}
                        pendingTrades={EMPTY_TRADES}
                        index={index}
                        currency={portfolioCurrency}
                        isMultiCurrencyPortfolio={isMultiCurrencyPortfolio}
                        highlightPendingWithdraw={false}
                      />
                    ))}
                </TableRow>
              )}

              {securities.map((security, index) => {
                const diffRow = diffRowBySecurityId.get(security.id);
                const holdingAmountDisplayMode =
                  diffRow != null && diffRow.deltaCents !== 0
                    ? 'proposedEnd'
                    : 'currentHolding';

                return (
                  <TableRow key={security.id} sx={{ height: auditRowHeight }}>
                    <SecurityTableRow
                      asTableCells
                      security={security}
                      subAccounts={security.subAccounts}
                      draftTrades={EMPTY_TRADES}
                      pendingTrades={EMPTY_TRADES}
                      index={index + 1}
                      currency={portfolioCurrency}
                      isMultiCurrencyPortfolio={isMultiCurrencyPortfolio}
                      hideCurrentChangeTooltip
                      isMakeHigher={showPreTradeDetail}
                      statusBadge={
                        diffRow
                          ? resolveAuditPositionStatusBadge(
                              diffRow,
                              t,
                              isCashEventRebalance,
                            )
                          : undefined
                      }
                      auditProposedSummary={auditProposedSummary}
                      auditSummaryColumnBorder={auditSummaryColumnBorder}
                      auditOmitSummaryColumn={showAuditDeviationColumn}
                    />
                    {showAuditDeviationColumn && (
                      <>
                        <AuditReportTargetCell
                          index={index + 1}
                          expectedValueCents={
                            diffRow
                              ? resolveAuditModelTargetCents(
                                  diffRow,
                                  proposedPoolScale,
                                )
                              : undefined
                          }
                          expectedPercentage={
                            diffRow
                              ? resolveAuditSnapshotTargetPercentage(diffRow)
                              : undefined
                          }
                        />
                        <AuditProposedCell
                          index={index + 1}
                          proposedCents={diffRow?.proposedCents}
                        />
                        <AuditModelDeviationCell
                          display={resolveAuditModelDeviationDisplay(
                            diffRow,
                            proposedPoolScale,
                          )}
                          index={index + 1}
                          borderRight={showSubAccountColumns}
                        />
                      </>
                    )}
                    {showSubAccountColumns &&
                      security.subAccounts.map((subAccount, subIndex) => {
                        const subAccountRow = diffRow?.subAccountRows.find(
                          (entry) => entry.subAccountId === subAccount.id,
                        );
                        const holdingValueCents =
                          subAccount.holding?.valueCents ?? 0;

                        return (
                          <SubAccountHoldingTableCell
                            key={holdingTradeKey(security.id, subAccount.id)}
                            security={security}
                            subAccount={subAccount}
                            draftTrades={draftTrades}
                            pendingTrades={EMPTY_TRADES}
                            handleHoldingChange={handleHoldingChange}
                            readOnly
                            index={index + 1}
                            subIndex={subIndex}
                            currency={security.currency}
                            originalValueCentsOverride={
                              subAccountRow?.runTimeCurrentCents ??
                              holdingValueCents
                            }
                            viewPreTradePositions={viewPreTradePositions}
                            isMakeHigher={showPreTradeDetail}
                            holdingAmountDisplayMode={holdingAmountDisplayMode}
                          />
                        );
                      })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Card>
    </Box>
  );
};
